import { matchPath } from "react-router";

import MenuItem from "../models/MenuItem";
import { validateUserPermission } from "./PermissionGuard";
import { getLegacyAdminHostname } from "./UtilityService";

const PATHS = (session: any) => [
  {
    paths: [
      "users",
      "/user/:user_id",
      "cohorts",
      "cohort/:cohort_id",
      "modules",
      "module/:module_id",
      "exercises",
      "/exercise/:exercise_id",
      "/modules/users/import",
      "programs",
      "/program/:program_id",
      "/cohorts/workload/leaders",
      "/cohorts/workload/program-managers",
    ],
    items: [
      { label: "COMPONENTS.NAVBAR_LEFT.USERS", to: "/users", iconClass: "dst-icon-users-01" },
      { label: "COMPONENTS.NAVBAR_LEFT.COHORTS", to: "/cohorts", iconClass: "dst-icon-bezier-curve-01" },
      validateUserPermission([{ methods: ["POST"], route: "/modules/users/batch" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.IMPORT_MODULES",
            to: "/modules/users/import",
            iconClass: "dst-icon-package-plus",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/modules" }], session.permissions)
        ? { label: "COMPONENTS.NAVBAR_LEFT.MODULES", to: "/modules", iconClass: "dst-icon-package" }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/exercises" }], session.permissions)
        ? { label: "COMPONENTS.NAVBAR_LEFT.EXERCISES", to: "/exercises", iconClass: "dst-icon-file-code-01" }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/programs" }], session.permissions)
        ? { label: "COMPONENTS.NAVBAR_LEFT.PROGRAMS", to: "/programs", iconClass: "dst-icon-list" }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/cohorts/workload/leaders" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.COHORTS_WORKLOAD_LEADERS",
            to: "/cohorts/workload/leaders",
            iconClass: "dst-icon-scales-02",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/cohorts/workload/program-managers" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.COHORTS_WORKLOAD_PROGRAM_MANAGERS",
            to: "/cohorts/workload/program-managers",
            iconClass: "dst-icon-scales-02",
          }
        : null,
    ],
  },
  {
    paths: [
      "insights",
      "/insights/surveys",
      "/insights/cohorts/surveys",
      "/insights/evaluation-accuracy",
      "insights/net-promoter-score",
    ],
    items: [
      { label: "COMPONENTS.NAVBAR_LEFT.USERS", to: "/insights", iconClass: "dst-icon-users-01" },
      {
        label: "COMPONENTS.NAVBAR_LEFT.SURVEYS",
        to: "/insights/surveys",
        iconClass: "dst-icon-file-question-02",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.COHORT_SURVEYS",
        to: "/insights/cohorts/surveys",
        iconClass: "dst-icon-file-question-02",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.EVALUATION_ACCURACY",
        to: "/insights/evaluation-accuracy",
        iconClass: "dst-icon-file-check-02",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.NET_PROMOTER_SCORE",
        to: "/insights/net-promoter-score",
        iconClass: "dst-icon-divide-03",
      },
    ],
  },
  {
    paths: ["/evaluation-attempt/:eval_id", "/evaluations/attempts/*", "/evaluation/:eval_id", "/evaluations/"],
    items: [
      {
        label: "COMPONENTS.NAVBAR_LEFT.EVALUATION_ATTEMPTS",
        to: "/evaluations/attempts",
        iconClass: "dst-icon-file-question-03",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.CORRECTIONS",
        to: "/evaluations/attempts?status=CORRECTION_PENDING,PRE_CORRECTED",
        iconClass: "dst-icon-file-check-02",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.EVALUATIONS",
        to: "/evaluations",
        iconClass: "dst-icon-list",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.RECRUITEMENTS",
        to: `${getLegacyAdminHostname()}/eval/recruitments`,
        iconClass: "dst-icon-graduation-hat-01",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.INVITE",
        to: `${getLegacyAdminHostname()}/eval/invite`,
        iconClass: "dst-icon-send-03",
      },
    ],
  },
  {
    paths: ["/invitations", "/courses", "/course/:course_id"],
    items: [
      { label: "COMPONENTS.NAVBAR_LEFT.INVITATIONS", to: "/invitations", iconClass: "dst-icon-send-03" },
      {
        label: "COMPONENTS.NAVBAR_LEFT.COURSES",
        to: "/courses",
        iconClass: "dst-icon-list",
      },
    ],
  },
  {
    paths: [
      "meetings",
      "/meetings/import",
      "/meeting/:meeting_id",
      "meetings/types",
      "/meetings/absence-proofs",
      "/meetings/workload",
      "/meetings/latest",
    ],
    items: [
      { label: "COMPONENTS.NAVBAR_LEFT.MEETINGS", to: "/meetings", iconClass: "dst-icon-calendar" },
      { label: "COMPONENTS.NAVBAR_LEFT.LATEST_MEETINGS", to: "/meetings/latest", iconClass: "dst-icon-calendar-date" },
      validateUserPermission([{ methods: ["GET"], route: "/meetings/types" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.MEETINGS_TYPES",
            to: "/meetings/types",
            iconClass: "dst-icon-list",
          }
        : null,
      validateUserPermission([{ methods: ["POST"], route: "/meetings/batch" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.IMPORT",
            to: "/meetings/import",
            iconClass: "dst-icon-package-plus",
          }
        : null,
      {
        label: "COMPONENTS.NAVBAR_LEFT.MY_MEETINGS_AS_ORGANIZER",
        to: `/meetings?authors=${encodeURIComponent(session.user_id)}&reset=true`,
        iconClass: "dst-icon-calendar-check-01",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.MY_MEETINGS_AS_ANIMATOR",
        to: `/meetings?animators=${encodeURIComponent(session.staff_id)}&reset=true`,
        iconClass: "dst-icon-presentation-chart-01",
      },
      validateUserPermission([{ methods: ["GET"], route: "/meetings/absence-proofs" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.ABSENCE_JUSTIFICATIONS",
            to: "/meetings/absence-proofs",
            iconClass: "dst-icon-calendar-check-02",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/meetings/workload" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.MEETINGS_WORKLOAD",
            to: "/meetings/workload",
            iconClass: "dst-icon-scales-02",
          }
        : null,
    ],
  },
  {
    paths: ["diplomas", "/diplomas/import"],
    items: [
      { label: "COMPONENTS.NAVBAR_LEFT.DIPLOMAS", to: "/diplomas", iconClass: "dst-icon-graduation-hat-01" },
      {
        label: "COMPONENTS.NAVBAR_LEFT.IMPORT",
        to: "/diplomas/import",
        iconClass: "dst-icon-package-plus",
      },
    ],
  },
  {
    paths: [
      "permissions",
      "/permissions/api-keys",
      "/permissions/staff-members",
      "/permissions/user-roles-scopes",
      "/permissions/import",
    ],
    items: [
      { label: "COMPONENTS.NAVBAR_LEFT.PERMISSIONS", to: "/permissions", iconClass: "dst-icon-shield-tick" },
      validateUserPermission([{ methods: ["GET"], route: "/staff-members" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.STAFF_MEMBERS",
            to: "/permissions/staff-members",
            iconClass: "dst-icon-tool-02",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/users/roles/scopes" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.USER_ROLES_SCOPES",
            to: "/permissions/user-roles-scopes",
            iconClass: "dst-icon-route",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/api-keys" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.API_KEYS",
            to: "/permissions/api-keys",
            iconClass: "dst-icon-key-01",
          }
        : null,
      validateUserPermission([{ methods: ["POST"], route: "/permissions/import" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.IMPORT",
            to: "/permissions/import",
            iconClass: "dst-icon-package-plus",
          }
        : null,
    ],
  },
  {
    paths: ["demo/records"],
    items: [{ label: "COMPONENTS.NAVBAR_LEFT.DEMO_RECORDS", to: "/demo/records", iconClass: "dst-icon-database-01" }],
  },
  {
    paths: [
      "/ephemeral/sessions",
      "/ephemeral/exercises",
      "/ephemeral/import",
      "/ephemeral/users",
      "/ephemeral/users/:ephemeral_user_id",
    ],
    items: [
      validateUserPermission([{ methods: ["GET"], route: "/ephemeral-evaluations" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.EPHEMERAL",
            to: "/ephemeral/sessions",
            iconClass: "dst-icon-clipboard-check",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/ephemeral-evaluations/exercises" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.EPHEMERAL_EXERCISES",
            to: "/ephemeral/exercises",
            iconClass: "dst-icon-list",
          }
        : null,
      validateUserPermission(
        [{ methods: ["POST"], route: "/ephemeral-evaluations/exercises/batch" }],
        session.permissions
      )
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.IMPORT_EXERCISES",
            to: "/ephemeral/import",
            iconClass: "dst-icon-package-plus",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/ephemeral-evaluations/users" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.EPHEMERAL_USERS",
            to: "/ephemeral/users",
            iconClass: "dst-icon-users-01",
          }
        : null,
    ],
  },
  {
    paths: ["/projects", "/project/:project_id", "/projects/subjects", "/projects/workload"],
    items: [
      {
        label: "COMPONENTS.NAVBAR_LEFT.PROJECTS",
        to: "/projects",
        iconClass: "dst-icon-folder",
      },
      {
        label: "COMPONENTS.NAVBAR_LEFT.MY_PROJECTS_AS_MENTOR",
        to: `/projects?mentors=${encodeURIComponent(session.user_id)}`,
        iconClass: "dst-icon-presentation-chart-01",
      },
      validateUserPermission([{ methods: ["GET"], route: "/projects/subjects" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.PROJECT_SUBJECTS",
            to: "/projects/subjects",
            iconClass: "dst-icon-folder-question",
          }
        : null,
      validateUserPermission([{ methods: ["GET"], route: "/projects/workload" }], session.permissions)
        ? {
            label: "COMPONENTS.NAVBAR_LEFT.PROJECT_WORKLOAD",
            to: "/projects/workload",
            iconClass: "dst-icon-scales-02",
          }
        : null,
    ],
  },
];

const provideLeftNavbarItems = (path: string, session: any): MenuItem[] => {
  const matching_group = PATHS(session).find((group) => group.paths.some((key) => matchPath(key, path)));

  return (matching_group?.items?.filter((elem) => !!elem) as MenuItem[]) || [];
};

export default provideLeftNavbarItems;
